import colors from "common/styles/colors";
import { createTheme, ThemeOptions } from "@mui/material/styles";
import theme from "../../../../../../common/styles/appTheme";

const tableThemeConfig = {
  ...theme,
  components: {
    MUIDataTable: {
      styleOverrides: {
        root: {
          border: `1px solid ${colors.lightGrey3}`,
          "& .Mui-selected": {
            backgroundColor: `${colors.lightViolet_100} !important`
          }
        },
        paper: {
          marginBottom: "20px",
          boxShadow: "none"
        },
        tableRoot: {
          borderBottom: "none"
        }
      }
    },
    MUIDataTableHead: {
      styleOverrides: {
        main: {
          borderBottom: `1px solid ${colors.lightGrey3}`
        }
      }
    },
    MUIDataTableHeadRow: {
      styleOverrides: {
        root: {
          backgroundColor: `${colors.lightGrey1} !important`
        }
      }
    },
    MUIDataTableHeadCell: {
      styleOverrides: {
        root: {
          borderRadius: 6
        },
        toolButton: {
          fontWeight: 500
        }
      }
    },
    MUIDataTableBodyRow: {
      styleOverrides: {
        root: {
          "&:nth-of-type(even)": {
            backgroundColor: colors.lightGrey1,
            padding: 0
          }
        },
        responsiveStacked: {
          "@media (max-width: 960px)": {
            borderTop: "none",
            borderBottom: `1px solid ${colors.lightGrey3}`,
            "&:last-child": {
              borderBottom: "none"
            }
          }
        }
      }
    },
    MUIDataTableBodyCell: {
      styleOverrides: {
        root: {
          "&:not(:first-of-type)": {
            borderLeft: `1px solid ${colors.lightGrey3}`
          }
        },
        stackedCommon: {
          borderLeft: `none !important`,
          fontSize: "14px !important",
          color: colors.darkGrey1,
          "&.datatables-noprint": {
            width: "100%"
          }
        },
        responsiveStackedSmallParent: {
          "& div:first-of-type": {
            fontWeight: 600
          }
        }
      }
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          "&$active": {
            color: colors.black,
            "&& $icon": {
              color: colors.black
            }
          }
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: "10px 14px",
          borderBottom: "none",
          "@media (min-width: 960px)": {
            "&:not(:last-child)": {
              borderRight: `1px solid ${colors.lightGrey3}`
            }
          }
        }
      }
    },
    MuiTypography: {
      styleOverrides: {
        body1: {
          fontSize: 14,
          fontWeight: 600,
          textAlign: "left",
          marginBottom: "8px"
        }
      }
    }
  }
};

export const tableTheme = createTheme(tableThemeConfig as ThemeOptions);

const muiDatatableThemeWithoutRowPaddingConfig = {
  ...tableThemeConfig,
  components: {
    ...tableThemeConfig.components,
    MUIDataTable: {
      ...tableThemeConfig.components.MUIDataTable,
      styleOverrides: {
        root: {
          ...tableThemeConfig.components.MUIDataTable.styleOverrides.root,
          borderBottom: "none !important",
          boxShadow: "none"
        }
      }
    },
    MUIDataTableHeadCell: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          borderLeft: `1px solid ${colors.lightGrey3}`,
          borderBottom: "none"
        }
      }
    },
    MuiTablePagination: {
      styleOverrides: {
        toolbar: {
          padding: "0px !important"
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: "3px 7px"
        },
        head: {
          backgroundColor: `${colors.lightGrey1} !important`,
          borderTop: `1px solid ${colors.lightGrey3}`,
          borderRadius: "0px !important"
        },
        footer: {
          outline: "1px solid white"
        }
      }
    }
  } as any
};

const muiDatatableThemeForDeviceTableConfig= {
  ...tableThemeConfig,
  components: {
    ...tableThemeConfig.components,
    MUIDataTable: {
      ...tableThemeConfig.components.MUIDataTable,
      styleOverrides: {
        root: {
          ...tableThemeConfig.components.MUIDataTable.styleOverrides.root,
          borderBottom: "none !important"
        },
        responsiveBase: {
          overflowX: "auto",

          "::-webkit-scrollbar": {
            height: "15px",
            border: "none"
          },
          "::-webkit-scrollbar-track": {
            border: "none",
            backgroundColor: colors.white,
            margin: "-1px"
          },
          "::-webkit-scrollbar-thumb": {
            borderRadius: "20px",
            backgroundColor: colors.grey[250],
            border: `4px solid ${colors.white}`,
            cursor: "pointer"
          },
          "::-webkit-scrollbar-thumb:hover": {
            backgroundColor: colors.grey[300]
          }
        },
        tableRoot: {
          width: "100%",
          borderBottom: `1px solid ${colors.grey[250]}`,
          borderRadius: '6px',
          ["@media (max-width: 992px)"]: {
            minWidth: 'auto'
          }
        }
      }
    },
    MUIDataTableHeadCell: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          borderLeft: `1px solid ${colors.lightGrey3}`,
          borderBottom: "none"
        },
        sortAction: {
          "&:sortAction": {
            display: "none",
          }
        }
      }
    },
    MuiTablePagination: {
      styleOverrides: {
        toolbar: {
          padding: "0px !important"
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: "3px 15px",
          borderBottom: "none"
        },
        paddingCheckbox: {
          width: "20px",
          height: "48px",
          padding: "0px"
        },
        head: {
          backgroundColor: `${colors.lightGrey1} !important`,
          borderTop: `1px solid ${colors.lightGrey3}`,
          borderRadius: "0px !important"
        },
        footer: {
          outline: "1px solid white"
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          padding: "5px 10px",
          fontSize: "12px"
        }
      }
    },
    MUIDataTableBodyRow: {
      styleOverrides: {
        root: {
          "&:nth-of-type(even), &:nth-of-type(even):hover": {
            backgroundColor: colors.lightGrey1,
            padding: 0
          },
          "&:nth-of-type(odd), &:nth-of-type(odd):hover": {
            backgroundColor: colors.white,
            padding: 0
          }
        }
      }
    },
    MUIDataTableSelectCell: {
      styleOverrides: {
        root: {
          backgroundColor: "inherit",
          "&:hover": {
            backgroundColor: "inherit"
          },
          "& div": {
            borderRight: `1px solid ${colors.grey[250]}`,
            width: "100%",
            height: "100%"
          }
        },
        fixedLeft: {
          position: 'relative'
        }
      }
    }
  } as any
};

const muiDatatableThemeForEmptyDataConfig = {
  ...muiDatatableThemeForDeviceTableConfig,
  components: {
    ...muiDatatableThemeForDeviceTableConfig.components,
    MUIDataTable: {
      ...muiDatatableThemeForDeviceTableConfig.components.MUIDataTable,
      styleOverrides: {
        ...muiDatatableThemeForDeviceTableConfig.components.MUIDataTable.styleOverrides,
        tableRoot: {
          ...muiDatatableThemeForDeviceTableConfig.components.MUIDataTable.styleOverrides.root,
          border: 'none',
          borderBottom: `1px solid ${colors.grey[250]}`,
          borderRadius: '6px',
          width: "100%"
        }
      }
    },
    MuiTableCell: {
      ...muiDatatableThemeForDeviceTableConfig.components.MuiTableCell,
      styleOverrides: {
        ...muiDatatableThemeForDeviceTableConfig.components.MuiTableCell.styleOverrides,
        root: {
          ...muiDatatableThemeForDeviceTableConfig.components.MuiTableCell.styleOverrides.root,
          padding: '15px 10px 10px'
        }
      }
    }
  }
};

export const muiDatatableThemeForDeviceTable = createTheme(muiDatatableThemeForDeviceTableConfig as ThemeOptions);

export const muiDatatableThemeForEmptyData = createTheme(muiDatatableThemeForEmptyDataConfig as ThemeOptions);

export const muiBadgesDataTableTheme = createTheme({
  ...muiDatatableThemeWithoutRowPaddingConfig,
  components: {
    ...muiDatatableThemeWithoutRowPaddingConfig.components,
    MUIDataTableHeadCell: {
      ...muiDatatableThemeWithoutRowPaddingConfig.components.MUIDataTableHeadCell,
      styleOverrides: {
        ...muiDatatableThemeWithoutRowPaddingConfig.components.MUIDataTableHeadCell.styleOverrides,
        root: {
          ...muiDatatableThemeWithoutRowPaddingConfig.components.MUIDataTableHeadCell.root,
          padding: "3px 8px",
          borderRadius: 6,
          borderLeft: `1px solid ${colors.lightGrey3}`,
          borderBottom: "none"
        }
      }
    },
    MUIDataTable: {
      styleOverrides: {
        ...muiDatatableThemeWithoutRowPaddingConfig.components.MUIDataTable.styleOverrides,
        tableRoot: {
          height: "100% !important",
          overflow: "hidden"
        },
        root: {
          ...muiDatatableThemeWithoutRowPaddingConfig.components.MUIDataTable.styleOverrides.root,
          "& .Mui-selected": {
            backgroundColor: `${colors.blue["100"]} !important`
          }
        }
      }
    },
    MUIDataTableBodyCell: {
      styleOverrides: {
        ...muiDatatableThemeWithoutRowPaddingConfig.components.MUIDataTableBodyCell.styleOverrides,
        root: {
          ...muiDatatableThemeWithoutRowPaddingConfig.components.MUIDataTableBodyCell.styleOverrides.root,
          height: "100%",
          "&:nth-child(5)": {
            wordBreak: "break-word"
          },
          ["@media (max-width: 992px)"]: {
            marginLeft: "5px !important",
            marginTop: "3px !important"
          }
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        ...muiDatatableThemeWithoutRowPaddingConfig.components.MuiTableCell.styleOverrides,
        root: {
          padding: 0,
          height: "100%"
        }
      }
    }
  }
});

export const muiDatatableThemeWithoutRowPadding = createTheme(muiDatatableThemeWithoutRowPaddingConfig as ThemeOptions);

const muiDatatableDeactivationConfig = {
  ...tableThemeConfig,
  components: {
    ...tableThemeConfig.components,
    MUIDataTable: {
      ...tableThemeConfig.components.MUIDataTable,
      styleOverrides: {
        root: {
          ...tableThemeConfig.components.MUIDataTable.styleOverrides.root,
          margin: '20px 0',
          border: `1px solid ${colors.lightGrey3}`,
          borderBottom: "none !important",
          boxShadow: "none",
          borderRadius: 6
        },
        responsiveBase: {
          borderRadius: 6
        }
      }
    },
    MUIDataTableHeadCell: {
      styleOverrides: {
        root: {
          width: '100%',
          borderRadius: 6,
          borderTop: "none",
          borderBottom: "none"
        }
      }
    },
    MuiTablePagination: {
      styleOverrides: {
        toolbar: {
          padding: "0px !important"
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: "3px 12px"
        },
        head: {
          backgroundColor: `${colors.lightGrey1} !important`,
          borderTop: `1px solid ${colors.lightGrey3}`,
          borderRadius: "0px !important",
          padding: '8px 0 8px 15px'
        },
        footer: {
          outline: "1px solid white"
        }
      }
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          display: 'block'
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          display: 'table',
          tableLayout: 'fixed',
          width: '100%'
        }
      }
    }
  } as any
};

const muiDatatableDeactivationConfigWithScroll = {
  ...muiDatatableDeactivationConfig,
  components: {
    ...muiDatatableDeactivationConfig.components,
    MuiTableBody: {
      ...muiDatatableDeactivationConfig.components.MuiTableBody,
      styleOverrides: {
        ...muiDatatableDeactivationConfig.components.MuiTableBody.styleOverrides,
        root: {
          ...muiDatatableDeactivationConfig.components.MuiTableBody.styleOverrides.root,
          display: 'block',
          maxHeight: '172px',
          overflowY: 'auto',
          scrollbarWidth: 'thin'
        }
      }
    }
  } as any
}

export const muiDatatableDeactivationModal = createTheme(muiDatatableDeactivationConfig as ThemeOptions);
export const muiDatatableDeactivationModalWithScroll = createTheme(muiDatatableDeactivationConfigWithScroll as ThemeOptions);

export const muiDatatableLogOutTheme = createTheme({
  ...tableThemeConfig,
  components: {
    ...muiDatatableThemeWithoutRowPadding.components,
    MUIDataTable: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          borderBottom: `1px solid ${colors.lightGrey3}`
        }
      }
    },
    MUIDataTableBodyRow: {
      styleOverrides: {
        root: {
          borderLeft: `1px solid ${colors.lightGrey3}`,
          borderRight: `1px solid ${colors.lightGrey3}`,
          "&:nth-of-type(even)": {
            backgroundColor: colors.lightGrey1,
            padding: 0
          }
        }
      }
    },
    MUIDataTableHeadCell: {
      styleOverrides: {
        root: {
          borderRight: `1px solid ${colors.lightGrey3}`,
          borderLeft: `1px solid ${colors.lightGrey3}`,
          padding: "3px 7px !important"
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        ...muiDatatableThemeWithoutRowPadding.components?.MuiTableCell?.styleOverrides,
        root: {
          ...tableThemeConfig.components?.MuiTableCell.styleOverrides.root,
          padding: "10px 14px !important"
        }
      }
    }
  } as any
});

export const muiDatatableZoneScanningBreakdownTheme = createTheme({
  ...tableThemeConfig,
  components: {
    ...tableTheme.components,
    MUIDataTable: {
      styleOverrides: {
        root: {
          border: `1px solid ${colors.lightGrey3}`,
          borderRadius: "6px",
          "& .Mui-selected": {
            backgroundColor: `${colors.lightViolet_100} !important`
          }
        }
      }
    },
    MUIDataTableHeadCell: {
      styleOverrides: {
        root: {
          borderRight: `1px solid ${colors.lightGrey3}`,
          borderLeft: `1px solid ${colors.lightGrey3}`,
          padding: "16x !important",
          fontSize: "16x !important"
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        ...tableTheme.components?.MuiTableCell?.styleOverrides,
        root: {
          ...tableThemeConfig.components?.MuiTableCell.styleOverrides.root,
          padding: "16px !important",
          fontSize: "16px !important"
        }
      }
    },
    MUIDataTableBodyCell: {
      styleOverrides: {
        ...tableTheme.components?.MuiTableCell,
        stackedCommon: {
          borderLeft: `none !important`,
          fontSize: "16px !important",
          color: colors.darkGrey1,
          "&.datatables-noprint": {
            width: "100%"
          }
        }
      }
    }
  } as any
});
